import React, { useEffect, useMemo, useState, useRef } from "react";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Divider,
  CircularProgress,
  TextField,
  Checkbox,
  FormControlLabel,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Link,
  Tooltip,
} from "@mui/material";
import axios from "axios";

import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import Swal from "sweetalert2";
import SignatureField from "../../../components/SignatureField";

const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_BASE_URL || process.env.REACT_APP_API_URL;

const pad2 = (n) => String(n).padStart(2, "0");

const fmtDate = (val) => {
  if (!val) return "—";
  const d = new Date(val);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
};

const fmtUsDateTimeStrict = (val) => {
  if (!val) return "—";
  const d = new Date(val);
  if (isNaN(d.getTime())) return "—";
  const mm = pad2(d.getMonth() + 1);
  const dd = pad2(d.getDate());
  const yyyy = d.getFullYear();
  let h = d.getHours();
  const AMPM = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  const hh = pad2(h);
  const min = pad2(d.getMinutes());
  return `${mm}/${dd}/${yyyy} ${hh}:${min} ${AMPM}`;
};

const extractSignedBy = (msg) => {
  if (!msg) return "";
  const m = String(msg).match(/digitally\s*signed\s*by\s*(.+?)\s*on\b/i);
  return m?.[1]?.trim() || "";
};

const ARMHSDischargeSummaryPreview = ({ patientFormId, data, onClose }) => {
  const [note, setNote] = useState(null);
  const [header, setHeader] = useState(null);
  const [formMeta, setFormMeta] = useState(null); // full form record from GetPatientFormsbyId
  const [clientSigSrc, setClientSigSrc] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const sigCanvasRef = useRef(null);
  const [openClientSignatureModal, setOpenClientSignatureModal] =
    useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);

  const resolvedPatientFormId = useMemo(
    () =>
      patientFormId ??
      data?.patientFormId ??
      data?.patientformID ??
      data?.id ??
      data?.Id ??
      null,
    [patientFormId, data]
  );

  const openClientModal = () => setOpenClientSignatureModal(true);
  const closeClientModal = () => {
    setOpenClientSignatureModal(false);
    if (sigCanvasRef.current) {
      sigCanvasRef.current.clear();
    }
  };

  const handleClose = () => {
    setShowSuccessAlert(false);

    if (onClose) {
      onClose();
    } else {
      try {
        const backdrop = document.querySelector(".MuiBackdrop-root");
        if (backdrop) {
          backdrop.click();
        } else {
          const closeButton = document.querySelector('[aria-label="close"]');
          if (closeButton) {
            closeButton.click();
          } else {
            window.history.back();
          }
        }
      } catch (err) {
        console.error("Could not close form:", err);
        window.location.reload();
      }
    }
  };

  useEffect(() => {
    let active = true;
    const controller = new AbortController();

    const run = async () => {
      if (!resolvedPatientFormId) {
        setError("Missing patientFormId");
        setLoading(false);
        return;
      }
      if (!API_BASE_URL) {
        setError("API base URL is not set.");
        setLoading(false);
        return;
      }
      const token = localStorage.getItem("neuro_patientportal_auth_token");
      if (!token) {
        setError("Not authenticated (missing token)");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");

        const headers = { Authorization: `Bearer ${token}` };

        const [dsRes, headerRes] = await Promise.all([
          axios.get(
            `${API_BASE_URL}/Rehabilitative/getDischargeSummaryPatientFormId`,
            {
              params: { patientformID: resolvedPatientFormId },
              headers,
              signal: controller.signal,
              validateStatus: (s) =>
                (s >= 200 && s < 300) || s === 204 || s === 404,
            }
          ),
          axios.get(`${API_BASE_URL}/Forms/GetPatientFormsbyId`, {
            params: { Id: resolvedPatientFormId },
            headers,
            signal: controller.signal,
            validateStatus: (s) =>
              (s >= 200 && s < 300) || s === 204 || s === 404,
          }),
        ]);

        if (!active) return;

        const dsNote =
          dsRes.status === 204 || dsRes.status === 404 ? null : dsRes.data;

        let hdrData =
          headerRes.status === 204 || headerRes.status === 404
            ? null
            : headerRes.data;

        // Handle possible array response from GetPatientFormsbyId
        const hdrObj = Array.isArray(hdrData)
          ? hdrData[0] || null
          : hdrData || null;

        setNote(dsNote);
        setHeader(hdrObj);
        setFormMeta(hdrObj);

        // ---- Signature load logic (Base64 first, then URL/path) ----
        const base64Candidate =
          dsNote?.clientsBase64 ||
          hdrObj?.clientsBase64 ||
          dsNote?.clientSignatureBase64 ||
          dsNote?.clientBase64;

        if (
          base64Candidate &&
          typeof base64Candidate === "string" &&
          base64Candidate.toLowerCase().includes("base64")
        ) {
          setClientSigSrc(base64Candidate);
        } else {
          const clientSigRaw =
            dsNote?.clientsURL ||
            hdrObj?.clientsURL ||
            dsNote?.clientSignatureURL ||
            dsNote?.patientSignatureURL ||
            "";

          if (clientSigRaw) {
            if (/^(https?:|data:|blob:)/i.test(clientSigRaw)) {
              if (active) setClientSigSrc(clientSigRaw);
            } else {
              try {
                const res = await fetch(
                  `${API_BASE_URL}/PatientDocument/ViewPatientDocument?path=${encodeURIComponent(
                    clientSigRaw
                  )}`,
                  {
                    headers: { Authorization: `Bearer ${token}` },
                    signal: controller.signal,
                  }
                );
                if (res.ok) {
                  const link = await res.text();
                  if (active && link) setClientSigSrc(link);
                }
              } catch {
                /* ignore */
              }
            }
          } else {
            setClientSigSrc("");
          }
        }

        if (!dsNote && !hdrObj) {
          setError("No Discharge Summary found for this form.");
        }
      } catch (err) {
        if (!active) return;
        console.error("Discharge Summary fetch error:", err);
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to fetch Discharge Summary."
        );
        setNote(null);
        setHeader(null);
        setFormMeta(null);
      } finally {
        if (active) setLoading(false);
      }
    };

    run();
    return () => {
      active = false;
      controller.abort();
    };
  }, [resolvedPatientFormId]);

  const handleSaveClientSignature = () => {
    if (!sigCanvasRef.current) return;

    const dataUrl = sigCanvasRef.current.getCanvas().toDataURL("image/png");
    setClientSigSrc(dataUrl);

    closeClientModal();
  };

  const handleDeleteClientSignature = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to remove this signature?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, remove it!",
      didOpen: () => {
        const swalEl = document.querySelector(".swal2-container");
        if (swalEl) {
          swalEl.style.zIndex = "9999";
        }
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        setClientSigSrc("");

        try {
          const token = localStorage.getItem(
            "neuro_patientportal_auth_token"
          );
          const client = axios.create({
            baseURL: API_BASE_URL,
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
              Accept: "application/json",
              "Content-Type": "application/json",
            },
          });

          let payload;
          if (formMeta) {
            payload = {
              ...formMeta,
              Id: formMeta.Id ?? formMeta.id ?? resolvedPatientFormId,
              patientFormId: resolvedPatientFormId,
              clientsBase64: "",
              clientsURL: "",
              patientSignatureURL: "",
              clientSignatureURL: "",
              imageURLClientSignature: "",
            };
          } else {
            // Fallback minimal payload
            payload = {
              patientFormId: resolvedPatientFormId,
              clientsBase64: "",
              clientsURL: "",
            };
          }

          await client.post("/Forms/AddPatientForm", payload);

          if (formMeta) {
            setFormMeta((prev) =>
              prev
                ? {
                    ...prev,
                    clientsBase64: "",
                    clientsURL: "",
                    patientSignatureURL: "",
                    clientSignatureURL: "",
                    imageURLClientSignature: "",
                  }
                : prev
            );
          }
          setHeader((prev) =>
            prev
              ? {
                  ...prev,
                  clientsBase64: "",
                  clientsURL: "",
                  patientSignatureURL: "",
                  clientSignatureURL: "",
                  imageURLClientSignature: "",
                }
              : prev
          );
          setNote((prev) =>
            prev
              ? {
                  ...prev,
                  clientsBase64: "",
                  clientsURL: "",
                  patientSignatureURL: "",
                  clientSignatureURL: "",
                  imageURLClientSignature: "",
                }
              : prev
          );

          Swal.fire({
            title: "Deleted!",
            text: "Signature has been removed.",
            icon: "success",
            timer: 1500,
            showConfirmButton: false,
            didOpen: () => {
              const swalEl = document.querySelector(".swal2-container");
              if (swalEl) {
                swalEl.style.zIndex = "9999";
              }
            },
          });
        } catch (e) {
          console.error(e);
          Swal.fire({
            title: "Error!",
            text: "Failed to remove signature.",
            icon: "error",
            didOpen: () => {
              const swalEl = document.querySelector(".swal2-container");
              if (swalEl) {
                swalEl.style.zIndex = "9999";
              }
            },
          });
        }
      }
    });
  };

  const handleSaveForm = async () => {
    if (!resolvedPatientFormId) return;
    try {
      const token = localStorage.getItem("neuro_patientportal_auth_token");
      const client = axios.create({
        baseURL: API_BASE_URL,
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      // Only treat as base64 if it's a data URL from canvas
      const isBase64 =
        clientSigSrc &&
        typeof clientSigSrc === "string" &&
        clientSigSrc.startsWith("data:");

      let payload;
      if (formMeta) {
        payload = {
          ...formMeta,
          Id: formMeta.Id ?? formMeta.id ?? resolvedPatientFormId,
          patientFormId: resolvedPatientFormId,
          ...(isBase64 && { clientsBase64: clientSigSrc }),
          // NOTE: we do NOT blank clientsURL here
        };
      } else {
        // Fallback minimal payload if formMeta missing
        payload = {
          patientFormId: resolvedPatientFormId,
          ...(isBase64 && { clientsBase64: clientSigSrc }),
        };
      }

      await client.post("/Forms/AddPatientForm", payload);

      if (formMeta && isBase64) {
        setFormMeta((prev) =>
          prev ? { ...prev, clientsBase64: clientSigSrc } : prev
        );
      }
      if (isBase64) {
        setHeader((prev) =>
          prev ? { ...prev, clientsBase64: clientSigSrc } : prev
        );
        setNote((prev) =>
          prev ? { ...prev, clientsBase64: clientSigSrc } : prev
        );
      }

      setShowSuccessAlert(true);
    } catch (e) {
      console.error(e);
      alert("Failed to save form.");
    }
  };

  if (loading)
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
      >
        <CircularProgress />
      </Box>
    );

  if (error)
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">{error}</Alert>
      </Box>
    );

  if (!note && !header)
    return (
      <Typography align="center" color="text.secondary" sx={{ mt: 4 }}>
        No data available.
      </Typography>
    );

  const headerTitle =
    header?.customFormTitle ||
    note?.customFormTitle ||
    "ARMHS Discharge Summary";
  const dos =
    note?.assessmentDate ||
    header?.assessmentDate ||
    header?.createdDate ||
    null;

  const fileNo =
    header?.fileNo ||
    header?.fileno ||
    header?.accountNo ||
    note?.fileno ||
    note?.fileNo ||
    note?.accountNo ||
    "—";
  const clientName = header?.patientName || note?.patientName || "—";
  const dob = note?.dOB || header?.dob || header?.dOB || null;

  const titleField = note?.title || header?.customFormTitle || headerTitle;

  const clientSigDate =
    note?.patientSignatureDATE ||
    header?.patientSignatureDATE ||
    note?.clientSignatureDATE ||
    header?.clientSignatureDATE ||
    null;

  const staffDateTime =
    note?.providerDate ||
    note?.professionalDate ||
    note?.staffsignatureDATE ||
    note?.staffSignatureDATE ||
    header?.providerDate ||
    header?.professionalDate ||
    header?.staffsignatureDATE ||
    null;

  const staffMsgCandidates = [
    note?.message,
    header?.message,
    note?.massagestaffSignature,
    header?.massagestaffSignature,
    note?.staffSignature,
    header?.staffSignature,
  ].filter(Boolean);

  const staffMessagePreferred =
    staffMsgCandidates.find((m) =>
      /digitally\s*signed\s*by/i.test(String(m))
    ) || staffMsgCandidates[0] || "";

  const staffSignedNameFromMsg = extractSignedBy(staffMessagePreferred);
  const directorSignedNameFromMsg = extractSignedBy(
    note?.messagetreatmentdirector || header?.messagetreatmentdirector || ""
  );

  const staffNameFallback =
    note?.mhPractitioner ||
    header?.mhPractitioner ||
    note?.practitionerName ||
    header?.practitionerName ||
    note?.professionalName ||
    header?.professionalName ||
    note?.staffSignatureName ||
    header?.staffSignatureName ||
    note?.staffName ||
    header?.staffName ||
    "";

  const staffNameForDisplay =
    staffSignedNameFromMsg || directorSignedNameFromMsg || staffNameFallback;

  const staffDisplay = (() => {
    const msg = String(staffMessagePreferred || "");
    if (/digitally\s*signed\s*by/i.test(msg)) return msg;
    const dt = fmtUsDateTimeStrict(staffDateTime);
    const hasDate = dt !== "—";
    const nm = (staffNameForDisplay || "").trim();
    if (nm && hasDate) return `Digitally signed by ${nm} on ${dt}.`;
    if (nm) return `Digitally signed by ${nm}.`;
    if (hasDate) return `Digitally signed on ${dt}.`;
    return msg || "—";
  })();

  const directorMessage =
    note?.messagetreatmentdirector || header?.messagetreatmentdirector || "";

  const headerFieldSx = {
    "& .MuiInputBase-root": {
      height: 40,
      fontSize: "0.9rem",
      padding: "0 10px",
    },
    "& .MuiInputLabel-root": { fontSize: "0.875rem" },
    "& .MuiSvgIcon-root": { display: "none" },
  };

  return (
    <>
      {showSuccessAlert && (
        <>
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: 1299,
            }}
          />
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "300px",
              padding: "20px",
              backgroundColor: "#fff",
              boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
              borderRadius: "8px",
              textAlign: "center",
              zIndex: 1300,
            }}
          >
            <Typography variant="h6" style={{ marginBottom: "10px" }}>
              Form Saved
            </Typography>
            <Typography style={{ marginBottom: "20px" }}>
              Form saved successfully
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={handleClose}
            >
              OK
            </Button>
          </div>
        </>
      )}

      <Paper
        sx={{
          p: 0,
          mb: 4,
          borderRadius: 2,
          boxShadow: "0 3px 8px rgba(0,0,0,0.08)",
        }}
      >
        <Box
          sx={{
            position: "sticky",
            top: "-16px",
            zIndex: (t) => t.zIndex.appBar,
            bgcolor: "#fff",
            p: { xs: 2, sm: 3, md: 4 },
            pb: 0,
            mb: 3,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              position: "absolute",
              top: { xs: 8, sm: 12, md: 16 },
              left: { xs: 8, sm: 16 },
              fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.875rem" },
              maxWidth: { xs: "55%", sm: "40%", md: "auto" },
            }}
          >
            <strong>DOS:</strong> {fmtDate(dos)}
          </Typography>

          <Typography
            variant="h6"
            sx={{
              position: "absolute",
              top: { xs: 8, sm: 12, md: 16 },
              left: "50%",
              transform: "translateX(-50%)",
              fontWeight: "bold",
              fontSize: { xs: "0.9rem", sm: "1.05rem", md: "1.25rem" },
              maxWidth: { xs: "90%", sm: "70%", md: "60%" },
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              textAlign: "center",
            }}
          >
            {headerTitle}
          </Typography>

          <Box sx={{ height: { xs: 24, sm: 28, md: 32 } }} />
          <Divider />
        </Box>

        <Box sx={{ px: 4 }}>
          <Grid
            container
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Grid item xs={12} md={4} sx={{ mb: { xs: 1, md: 0 } }}>
              <Typography variant="body2">
                <strong>File #:</strong> {fileNo}
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
              sx={{ textAlign: "center", mb: { xs: 1, md: 0 } }}
            >
              <Typography variant="body2">
                <strong>Client:</strong> {clientName}
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
              sx={{ textAlign: { xs: "left", md: "right" } }}
            >
              <Typography variant="body2">
                <strong>DOB:</strong> {fmtDate(dob)}
              </Typography>
            </Grid>
          </Grid>
          <Divider sx={{ mb: 3 }} />
        </Box>

        <Box sx={{ px: 4, pb: 4 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
              },
              gap: 4,
              mb: 2,
            }}
          >
            <TextField
              label="Title"
              value={titleField || "—"}
              size="small"
              fullWidth
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              sx={headerFieldSx}
            />
            <TextField
              label="Assessment Date"
              value={
                fmtDate(note?.assessmentDate || header?.assessmentDate) || "—"
              }
              size="small"
              fullWidth
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              sx={headerFieldSx}
            />
          </Box>

          <Grid container spacing={1} sx={{ mb: 2 }}>
            {[
              {
                label: "Printed and Filed",
                value: note?.isPrinted ?? header?.isPrinted,
              },
              { label: "Inactive", value: note?.inactive ?? header?.inactive },
              {
                label: "Audit Ready",
                value: note?.isAuditReady ?? header?.isAuditReady,
              },
              { label: "Lock", value: note?.isChecked ?? header?.isChecked },
            ].map((item, idx) => (
              <Grid item xs={12} sm={6} md="auto" key={idx}>
                <FormControlLabel
                  control={<Checkbox checked={!!item.value} disabled />}
                  label={item.label}
                />
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
            Referred by:
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControlLabel
                control={<Checkbox checked={!!note?.isParent} disabled />}
                label="Parent"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControlLabel
                control={<Checkbox checked={!!note?.isCourt} disabled />}
                label="Court Ordered"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControlLabel
                control={<Checkbox checked={!!note?.isMental} disabled />}
                label="Mental Health Professional"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControlLabel
                control={<Checkbox checked={!!note?.isCounty} disabled />}
                label="County Social Worker / Probation Officer"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControlLabel
                control={<Checkbox checked={!!note?.isOther} disabled />}
                label="Other"
              />
            </Grid>
          </Grid>

          {note?.otherDetail && (
            <TextField
              label="Other Detail"
              value={note.otherDetail}
              fullWidth
              multiline
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              sx={{ my: 2 }}
            />
          )}

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            ARMHS Services Provided:
          </Typography>

          <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
            Psychotherapy
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              columnGap: 2,
              rowGap: 0.5,
              mb: 2,
            }}
          >
            <FormControlLabel
              control={<Checkbox checked={!!note?.isindividual} disabled />}
              label="Individual"
            />
            <FormControlLabel
              control={<Checkbox checked={!!note?.isfamily} disabled />}
              label="Family"
            />
            <FormControlLabel
              control={<Checkbox checked={!!note?.isgroup} disabled />}
              label="Group"
            />
          </Box>

          <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
            Skills
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              columnGap: 2,
              rowGap: 0.5,
              mb: 2,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox checked={!!note?.isSkillsindividual} disabled />
              }
              label="Individual"
            />
            <FormControlLabel
              control={<Checkbox checked={!!note?.isSkillsfamily} disabled />}
              label="Family"
            />
            <FormControlLabel
              control={<Checkbox checked={!!note?.isSkillsgroup} disabled />}
              label="Group"
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="h6" sx={{ mt: 2, mb: 1.5 }}>
            1. Client Problem(s) and Diagnosis
          </Typography>

          <TextField
            label="Client Problem(s)"
            value={note?.clientsProblems || ""}
            fullWidth
            multiline
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
            sx={{ my: 2 }}
          />

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            DSM 5 Diagnosis
          </Typography>

          {note?.dsM5Diagnosislist?.map((d, i) => (
            <Box key={i} sx={{ pl: 2, mt: i === 0 ? 0.5 : 0, mb: 3 }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, 1fr)",
                  },
                  gap: 4,
                  mb: 1.5,
                }}
              >
                <TextField
                  label="DSM 5"
                  value={d?.dSM5 || ""}
                  fullWidth
                  multiline
                  minRows={1}
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{ shrink: true }}
                />

                <TextField
                  label="DSM 5 Detail"
                  value={d?.dSM5Detail || ""}
                  fullWidth
                  multiline
                  minRows={1}
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{ shrink: true }}
                />

                <TextField
                  label="ICD 10"
                  value={d?.iCD10 || ""}
                  fullWidth
                  multiline
                  minRows={1}
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{ shrink: true }}
                />

                <TextField
                  label="ICD 10 Detail"
                  value={d?.iCD10Detail || ""}
                  fullWidth
                  multiline
                  minRows={1}
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>

              <TextField
                label="Specifier"
                value={d?.specifier || ""}
                fullWidth
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 1 }}
              />
              <TextField
                label="Explain (Specifier)"
                value={d?.specifierExplain || ""}
                fullWidth
                multiline
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 1.5 }}
              />

              <TextField
                label="Subtype"
                value={d?.subtype || ""}
                fullWidth
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 1 }}
              />
              <TextField
                label="Explain (Subtype)"
                value={d?.subtypeExplain || ""}
                fullWidth
                multiline
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 1.5 }}
              />

              <TextField
                label="Severity"
                value={d?.severity || ""}
                fullWidth
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 1 }}
              />
              <TextField
                label="Explain (Severity)"
                value={d?.severityExplain || ""}
                fullWidth
                multiline
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 1.5 }}
              />

              <TextField
                label="Other Factors"
                value={d?.otherFactors || ""}
                fullWidth
                multiline
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          ))}

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ mb: 1.5 }}>
            Supportive Assessment(s)/ Functional Assessment(s) and Screening(s)
          </Typography>

          {note?.supportiveAssessmentlist?.length ? (
            note.supportiveAssessmentlist.map((s, idx) => (
              <Box key={idx} sx={{ mb: 3, pl: 2 }}>
                <Grid container spacing={2} sx={{ mb: 1 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      label="Date"
                      value={fmtDate(s?.date)}
                      fullWidth
                      InputProps={{ readOnly: true }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="Name"
                      value={s?.name || ""}
                      fullWidth
                      InputProps={{ readOnly: true }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <TextField
                      label="Description"
                      value={s?.description || ""}
                      fullWidth
                      InputProps={{ readOnly: true }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
                <TextField
                  label="Notes"
                  value={s?.notes || ""}
                  fullWidth
                  multiline
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              No supportive assessments recorded.
            </Typography>
          )}

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6">
            2. Treatment Summary (methods, duration, visits)
          </Typography>

          <Grid container spacing={2} sx={{ my: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Total Sessions"
                value={note?.totalSessions || ""}
                fullWidth
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Individual Sessions"
                value={note?.individualSessions || ""}
                fullWidth
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Family Sessions"
                value={note?.familySessions || ""}
                fullWidth
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Group Sessions"
                value={note?.groupSessions || ""}
                fullWidth
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          <TextField
            label="Goals and Objectives met"
            value={note?.goalsmet || ""}
            fullWidth
            multiline
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
            sx={{ my: 1 }}
          />
          <TextField
            label="Goals and Objectives not met"
            value={note?.goalsnotmet || ""}
            fullWidth
            multiline
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
            sx={{ my: 1 }}
          />

          <TextField
            label="Summary"
            value={note?.summary || ""}
            fullWidth
            multiline
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
            sx={{ my: 1 }}
          />

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6">3. Outcome (Current condition)</Typography>
          <TextField
            value={note?.outComes || ""}
            fullWidth
            multiline
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
            sx={{ my: 1 }}
          />

          <Typography variant="h6" sx={{ mt: 2 }}>
            4. Reason For Termination
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap" }}>
            {[
              { label: "Client Discontinued", key: "isPatientDiscontinued" },
              { label: "Transferred", key: "isTransferOther" },
              { label: "Treatment goals met", key: "isTreatmentMet" },
              { label: "Relocated", key: "isRelocated" },
              { label: "Other", key: "other" },
            ].map((item, idx) => (
              <FormControlLabel
                key={idx}
                control={<Checkbox checked={!!note?.[item.key]} disabled />}
                label={item.label}
                sx={{ mr: 2 }}
              />
            ))}
          </Box>
          {note?.explainOther && (
            <TextField
              label="Explain"
              value={note.explainOther}
              fullWidth
              multiline
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              sx={{ my: 2 }}
            />
          )}

          <Typography variant="h6" sx={{ mt: 2 }}>
            5. Treatment Recommendations/Referrals
          </Typography>
          <TextField
            value={note?.refferals || ""}
            fullWidth
            multiline
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
            sx={{ my: 1 }}
          />

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ mb: 1 }}>
            Signatures
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 2,
              flexWrap: "wrap",
              mb: 2,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ minWidth: { xs: "100%", sm: 220 } }}
            >
              <strong>Client Signature:</strong>
            </Typography>
            {clientSigSrc ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <img
                  src={clientSigSrc}
                  alt="Client signature"
                  style={{
                    width: 200,
                    height: 80,
                    border: "2px solid #000",
                    background: "#f5f5f5",
                    padding: 10,
                    borderRadius: 8,
                    objectFit: "contain",
                  }}
                />
                {clientSigDate ? (
                  <Typography sx={{ fontWeight: 600 }}>
                    Signed on {fmtUsDateTimeStrict(clientSigDate)}
                  </Typography>
                ) : null}
                <Tooltip title="Edit Signature" arrow>
                  <Button
                    onClick={openClientModal}
                    variant="outlined"
                    size="small"
                    style={{ minWidth: "32px", padding: "4px" }}
                    aria-label="Edit Signature"
                  >
                    <EditIcon />
                  </Button>
                </Tooltip>

                <Tooltip title="Remove Signature" arrow>
                  <Button
                    onClick={handleDeleteClientSignature}
                    style={{ minWidth: "32px", padding: "4px" }}
                    aria-label="Remove Signature"
                  >
                    <RemoveCircleOutlineIcon style={{ color: "red" }} />
                  </Button>
                </Tooltip>
              </Box>
            ) : (
              <Link component="button" onClick={openClientModal}>
                Open signing window
              </Link>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 2,
              flexWrap: "wrap",
              mb: 2,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ minWidth: { xs: "100%", sm: 220 } }}
            >
              <strong>Staff Signature:</strong>
            </Typography>
            <Typography sx={{ fontWeight: 600 }}>{staffDisplay}</Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ minWidth: { xs: "100%", sm: 220 } }}
            >
              <strong>ARMHS Treatment Director:</strong>
            </Typography>
            <Typography sx={{ fontWeight: 600 }}>
              {directorMessage || "—"}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveForm}
            >
              Save Form
            </Button>
          </Box>
        </Box>
      </Paper>

      <Dialog
        open={openClientSignatureModal}
        onClose={closeClientModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Signature</DialogTitle>
        <DialogContent>
          <Box sx={{ border: "1px solid #000", mt: 1 }}>
            <SignatureField
              ref={sigCanvasRef}
               
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button
            variant="contained"
            onClick={handleSaveClientSignature}
            sx={{ minWidth: 120, mr: 2 }}
          >
            Save
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (sigCanvasRef.current) {
                sigCanvasRef.current.clear();
              }
            }}
            sx={{ minWidth: 120 }}
          >
            Clear
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ARMHSDischargeSummaryPreview;